import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import { kycReviewService, type KycStatus } from '@/services/brokerKyc';
import { kycStatusLabel } from '@/pages/broker/brokerKycForm';
import { documentStateLabel, dossierStatusTone } from './kycReview';
import { formatDate } from '@/utils';

/**
 * #389: the country admin's list of broker compliance / KYC dossiers. The
 * server scopes it to the admin's country; a broker-side login gets a 403.
 */

const FILTERS: { value: '' | KycStatus; label: string }[] = [
  { value: 'submitted', label: 'Awaiting review' },
  { value: 'changes_requested', label: 'Changes requested' },
  { value: 'verified', label: 'Verified' },
  { value: '', label: 'All submitted dossiers' },
  { value: 'incomplete', label: 'Not yet submitted' },
];

function formatSubmitted(value: string | null): string {
  return value ? formatDate(value) || '-' : '-';
}

export function KycReviewQueuePage() {
  const [status, setStatus] = useState<'' | KycStatus>('submitted');
  const queue = useQuery({
    queryKey: ['kyc-reviews', status],
    queryFn: () => kycReviewService.queue(status || undefined),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">KYC reviews</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Compliance dossiers brokers submitted from their Compliance / KYC tab.
          </p>
        </div>
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Show
          <select
            aria-label="Dossier status"
            className="ml-2 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | KycStatus)}
          >
            {FILTERS.map((f) => (
              <option key={f.value || 'all'} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Card className="p-0">
        {queue.isLoading ? (
          <p className="p-6 text-sm text-gray-500">Loading dossiers...</p>
        ) : queue.isError ? (
          <div role="alert" className="flex items-center gap-3 p-6 text-sm text-red-600">
            <span>Could not load the dossiers: {getErrorMessage(queue.error)}</span>
            <button type="button" className="underline" onClick={() => queue.refetch()}>
              Retry
            </button>
          </div>
        ) : !queue.data?.length ? (
          <p className="p-6 text-sm text-gray-500">No dossiers in this list.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3">Broker</th>
                <th className="px-4 py-3">Dossier</th>
                <th className="px-4 py-3">Documents</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {queue.data.map((row) => (
                <tr key={row.broker}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {row.broker_name}
                    </div>
                    <div className="text-xs text-gray-500">{row.broker_code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge size="sm" variant={dossierStatusTone(row.kyc_status)}>
                      {kycStatusLabel(row.kyc_status).label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {documentStateLabel(row.documents.state)}
                    <div className="text-xs text-gray-500">
                      {row.documents.counts.pending} to review · {row.documents.counts.verified}{' '}
                      approved ·{' '}
                      {row.documents.counts.rejected + row.documents.counts.changes_requested} sent
                      back
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {formatSubmitted(row.kyc_submitted_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      className="text-primary-600 hover:underline"
                      to={`/country/kyc-reviews/${row.broker}`}
                    >
                      Review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
